import { useQuery } from '@tanstack/react-query';
import strapiClient from '../services/strapi/config';

/**
 * Hook personnalisé pour récupérer les articles du blog (newsletters)
 */
export const useBlogQuery = ({
    page = 1,
    pageSize = 12,
    sort = 'DateEnvoi:desc',
    search = '',
    categorie = 'toutes'
}) => {
    // Nettoyer le terme de recherche
    const cleanedSearch = search?.trim() || '';

    return useQuery({
        queryKey: ['blog-posts', page, pageSize, sort, cleanedSearch, categorie],
        queryFn: async () => {
            // Construction de l'URL avec les paramètres
            let url = '/api/blog/newsletters?';

            // Pagination
            url += `page=${page}&`;
            url += `pageSize=${pageSize}&`;

            // Tri
            url += `sort=${sort}&`;

            // Catégorie (si différente de 'toutes')
            if (categorie && categorie !== 'toutes') {
                url += `categorie=${encodeURIComponent(categorie)}&`;
            }

            // Recherche (si fournie et non vide)
            if (cleanedSearch) {
                url += `search=${encodeURIComponent(cleanedSearch)}&`;
            }

            const response = await strapiClient.get(url);

            return response.data;
        },
        staleTime: 30 * 1000, // 30 secondes au lieu de 5 minutes
        cacheTime: 10 * 60 * 1000, // 10 minutes
        keepPreviousData: false, // ✅ CHANGEMENT : false au lieu de true
        refetchOnWindowFocus: false,
        // Ne pas exécuter la requête si les paramètres sont invalides
        enabled: page > 0 && pageSize > 0,
        // Réessayer en cas d'erreur réseau
        retry: 3,
        retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
    });
};

/**
 * Hook pour récupérer les catégories disponibles avec leurs métadonnées
 */
export const useBlogCategoriesQuery = () => {
    return useQuery({
        queryKey: ['blog-categories'],
        queryFn: async () => {
            const response = await strapiClient.get('/api/blog/categories');
            // L'endpoint retourne maintenant des objets avec id, Nom, Slug, Couleur, etc.
            return response.data.data;
        },
        staleTime: 10 * 60 * 1000, // 10 minutes
        cacheTime: 30 * 60 * 1000, // 30 minutes
        refetchOnWindowFocus: false,
        retry: 2,
    });
};

/**
 * Hook pour récupérer toutes les catégories (pas seulement celles avec des newsletters)
 */
export const useAllNewsletterCategoriesQuery = () => {
    return useQuery({
        queryKey: ['all-newsletter-categories'],
        queryFn: async () => {
            const response = await strapiClient.get('/api/newsletter-categories', {
                params: {
                    'sort': 'OrdreAffichage,Nom',
                    'filters[Active][$eq]': true,
                    'populate': 'newsletters'
                }
            });

            // Transformer les données pour le format attendu
            return response.data.data.map(item => ({
                id: item.id,
                ...item.attributes,
                nombreNewsletters: item.attributes.newsletters?.data?.length || 0
            }));
        },
        staleTime: 15 * 60 * 1000, // 15 minutes
        cacheTime: 30 * 60 * 1000, // 30 minutes
        refetchOnWindowFocus: false,
        retry: 2,
    });
};

/**
 * Hook pour récupérer une catégorie par son slug
 */
export const useNewsletterCategoryBySlugQuery = (slug) => {
    return useQuery({
        queryKey: ['newsletter-category-by-slug', slug],
        queryFn: async () => {
            if (!slug) {
                throw new Error('Slug requis');
            }

            const response = await strapiClient.get(`/api/newsletter-categories/slug/${slug}`);

            // Transformer les données pour le format attendu
            const categoryData = response.data.data;
            return {
                id: categoryData.id,
                ...categoryData.attributes,
                newsletters: categoryData.attributes.newsletters?.data?.map(newsletter => ({
                    id: newsletter.id,
                    ...newsletter.attributes
                })) || []
            };
        },
        staleTime: 5 * 60 * 1000, // 5 minutes
        cacheTime: 15 * 60 * 1000, // 15 minutes
        enabled: !!slug, // Ne pas exécuter si pas de slug
        retry: 2,
    });
};

/**
 * Hook pour récupérer les statistiques des catégories
 */
export const useNewsletterCategoriesStatsQuery = () => {
    return useQuery({
        queryKey: ['newsletter-categories-stats'],
        queryFn: async () => {
            const response = await strapiClient.get('/api/newsletter-categories', {
                params: {
                    'populate[newsletters][filters][VisibleBlog][$eq]': true,
                    'populate[newsletters][filters][Statut][$eq]': 'Envoyée',
                    'populate[newsletters][fields][0]': 'id',
                    'sort': 'OrdreAffichage,Nom'
                }
            });

            // Calculer les statistiques pour chaque catégorie
            return response.data.data.map(item => ({
                id: item.id,
                nom: item.attributes.Nom,
                slug: item.attributes.Slug,
                couleur: item.attributes.Couleur,
                icone: item.attributes.Icone,
                nombreNewsletters: item.attributes.newsletters?.data?.length || 0,
                active: item.attributes.Active
            }));
        },
        staleTime: 10 * 60 * 1000, // 10 minutes
        cacheTime: 30 * 60 * 1000, // 30 minutes
        refetchOnWindowFocus: false,
        retry: 2,
    });
};

/**
 * Hook personnalisé pour les filtres du blog
 */
export const useBlogFilters = () => {
    const { data: categories = [] } = useBlogCategoriesQuery();

    // Options de tri disponibles
    const sortOptions = [
        { value: 'DateEnvoi:desc', label: 'Plus récentes' },
        { value: 'DateEnvoi:asc', label: 'Plus anciennes' },
        { value: 'Sujet:asc', label: 'Titre A-Z' },
        { value: 'Sujet:desc', label: 'Titre Z-A' }
    ];

    // Options de catégories avec "Toutes les catégories" en premier
    const categoryOptions = [
        { value: 'toutes', label: 'Toutes les catégories', count: null },
        ...categories.map(cat => ({
            value: cat.Slug || cat.slug,
            label: cat.Nom || cat.nom,
            count: cat.nombreNewsletters,
            couleur: cat.Couleur || cat.couleur,
            icone: cat.Icone || cat.icone
        }))
    ];

    return {
        sortOptions,
        categoryOptions,
        categories,
        isLoadingCategories: false // On peut ajouter le loading state si nécessaire
    };
};